import {useAuthorFollow} from "@/src/features/following/hooks";
import { boostPost, feedBoostEnabled } from "@/src/services/api/posts/feed-functions";
import { useQueryClient } from "@tanstack/react-query";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import Toast from "react-native-toast-message";
import LinkifiedText from "@/src/components/ui-kit/linkified-text";
import FormattedText from "@/src/components/ui-kit/formatted-text";
import { useAuth } from "@/src/contexts/auth.context";
import { usePostCard } from "@/src/hooks/features/posts/use-post-card";
import { usePermissions } from "@/src/hooks/use-permissions";
import { Post } from "@/src/models/post.model";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import { useRouter } from "expo-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import ShareModal from "../action-modals/share-modal";
import PostDetailModal from "./post-detail-modal";
import { FileAttachment } from "./post/file-attachment";
import PostActions from "./post/post-actions";
import StickerOverlay from "./stickers/sticker-overlay";
import StickerTray from "./stickers/sticker-tray";
import { containRect } from "./stickers/sticker-geometry";
import { useStickerSession } from "@/src/hooks/features/posts/use-sticker-session";
import { useStickersOn } from "@/src/features/sticker-display/use-stickers-on";
import StickerLayer from "@/src/features/sticker-display/sticker-layer";
import PostHeader from "./post/post-header";
import NewsPostBanner from "./post/news-post-banner";
import VideoPreview from "./videos/video-preview";
import TagPill from "./tag-pill";
import MentionedUserPill from "./mentioned-user-pill";
import LinkPreviewCard from "./post/link-preview/link-preview-card";
import { LinkPreviewSkeleton } from "./post/link-preview/link-preview-skeleton";
import { useLinkPreview } from "@/src/hooks/features/posts/use-link-preview";
import { isNewsPost } from "@/src/utils/news-post.utils";
import { flattenForClamp } from "@/src/utils/string.utils";
import { toPlainFormattedText } from "@/src/utils/rich-text.utils";
import {
  detectMediaKind,
  extractFirstUrl,
  isOnlyUrl,
} from "@/src/utils/link-preview.utils";
dayjs.extend(relativeTime);

type PostProps = Readonly<{
  post: Post;
  onEdit?: (post: Post) => void;
  onDelete?: (post: Post) => void;
  onReactorCount?: () => void;
}>;

const PostCard = ({ post, onEdit, onDelete, onReactorCount }: PostProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [boosting, setBoosting] = useState(false);
  const changeBoost = async (hours: 24 | 48, remove = false) => {
    if (boosting) return;
    setBoosting(true);
    try {
      await boostPost(post.ItemId, hours, remove);
      await queryClient.resetQueries({queryKey: [POSTS_QUERY_KEY]});
      Toast.show({type: "success", text1: t("FEED_BOOST_UPDATED", {defaultValue: "Highlight updated"})});
    } catch {
      Toast.show({type: "error", text1: t("FEED_BOOST_FAILED", {defaultValue: "Could not update highlight. Please try again."})});
    } finally {setBoosting(false);}
  };
  const { themeVars } = useTheme();
  const { mutate: sendNotification } = useSendNotification();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [cardWidth, setCardWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(300);

  const postCard = usePostCard(post, { onEdit, onDelete });
  const follow=useAuthorFollow(post.CreatedBy);
  const isNewsArticle = isNewsPost(post);
  // Like the Selisian feed: no card for news mirrors or posts with photos/videos.
  const hasMediaAttachments = (post.attachmentUrls ?? []).some((url) =>
    /\.(jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i.test(url),
  );
  const { data: linkPreview, isFetching: isLinkPreviewLoading } = useLinkPreview(
    post.content ?? "",
    { enabled: !isNewsArticle && !hasMediaAttachments },
  );

  // A URL in the post body that points straight at an image or video renders
  // inline, with no metadata fetch — `useLinkPreview` is deliberately disabled
  // for those kinds, so relying on `linkPreview` alone showed nothing at all for
  // the most common case. Mirrors what the composer previews before posting.
  const contentUrl = extractFirstUrl(post.content ?? "");
  const contentMediaKind = contentUrl ? detectMediaKind(contentUrl) : null;

  const { canUpdate, canDelete, isSuperAdmin } = usePermissions();
  const isOwner = post?.CreatedBy === user?.itemId;
  // A post that is only the URL shows just the card (Selisian parity).
  const showBody =
    !!post.content &&
    !(linkPreview && contentUrl && isOnlyUrl(post.content, contentUrl));

  // Collapsed posts render with their line breaks folded into spaces so the
  // clamp fills every visible line and the "…" always lands at the end of the
  // last one. Expanding swaps back to the authored text. Both the measuring copy
  // and the visible copy use the same string, so "See more" only appears when
  // the *preview* is genuinely longer than MAX_LINES.
  const previewContent = flattenForClamp(post.content);
  // Marker-stripped text for the invisible measuring copy below.
  const measureContent = isNewsArticle
    ? toPlainFormattedText(previewContent)
    : previewContent;
  const CaptionText = isNewsArticle ? FormattedText : LinkifiedText;

  const handleShareNotification = () => {
    if (post.authorSnapshot?.userId === user?.itemId) return;
    sendNotification({
      userIds: [post.authorSnapshot.userId],
      denormalizedPayload: JSON.stringify({
        type: "post_share",
        message: `${user?.firstName} ${user?.lastName} shared your post`,
        actorAvatar: user?.profileImageUrl,
        targetEntityId: post.ItemId,
      }),
      configurationName: "post-share",
      responseKey: "post-action",
      responseValue: "share",
    });
  };
  const [imageAspectRatio, setImageAspectRatio] = useState(1);
  const sticker = useStickerSession(post);
  const stickersOn = useStickersOn();
  const stickerMode = stickersOn && sticker.active;
  // Outside sticker mode everyone's stickers are still drawn (read-only) on the shown image or the body.
  const shownMediaIndex = postCard.firstMedia ? (post.attachmentUrls ?? []).indexOf(postCard.firstMedia) : -1;
  const showStickers = stickersOn && !stickerMode && (post.stickers?.length ?? 0) > 0;
  // In sticker mode the card shows the image being stickered (any image attachment).
  const stickerImage = stickerMode && sticker.imageIndexes.length ? post.attachmentUrls![sticker.mediaIndex] : null;
  const shownMedia = stickerImage ?? postCard.firstMedia;
  const [mediaBox, setMediaBox] = useState({ w: 0, h: 0 });
  const [bodyBox, setBodyBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (stickerImage) Image.getSize(stickerImage, (w, h) => setImageAspectRatio(w / h || 1), () => setImageAspectRatio(1));
  }, [stickerImage]);
  return (
    <View className="relative">
      {postCard.menuOpen && (
        <Pressable
          className="fixed inset-0 z-40"
          onPress={() => postCard.setMenuOpen(false)}
        />
      )}
      <View
        className="bg-chalk overflow-hidden rounded-xl md:rounded-2xl md:border md:border-ash-3"
        onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}
      >
        <NewsPostBanner post={post} />

        {post.lifeEventType && (
          <View className="flex-row items-center gap-2 px-4 py-2 bg-primary/10 border-b border-ash-3">
            <Text className="text-primary text-xs font-semibold">
              {post.lifeEventType === 'Promotion' ? '🎉' :
               post.lifeEventType === 'Education' ? '🎓' :
               post.lifeEventType === 'Relationship' ? '❤️' :
               post.lifeEventType === 'NewJob' ? '💼' :
               post.lifeEventType === 'Moved' ? '🏠' : '⭐'}{' '}
              {t(`LABEL_LIFE_EVENT_${post.lifeEventType.toUpperCase()}`)}
            </Text>
            {post.lifeEventDate ? (
              <Text className="text-xs text-gray-dark">
                · {dayjs(post.lifeEventDate).format('MMM D, YYYY')}
              </Text>
            ) : null}
          </View>
        )}
        <PostHeader
          post={post}
          menuItems={[
            ...(follow.available && !isOwner && follow.ready ? [{label:t(follow.following?"FOLLOW_UNFOLLOW":"FOLLOW_ACTION"),onPress:()=>{postCard.setMenuOpen(false);follow.mutation.mutate({desiredFollowing:!follow.following});}}] : []),
            ...(isAdmin && feedBoostEnabled && !boosting ? [
              {label: t("FEED_BOOST_24", {defaultValue: "Boost for 24 hours"}), onPress: () => {void changeBoost(24);}},
              {label: t("FEED_BOOST_48", {defaultValue: "Boost / extend for 48 hours"}), onPress: () => {void changeBoost(48);}},
              {label: t("FEED_BOOST_REMOVE", {defaultValue: "Remove boost"}), onPress: () => {void changeBoost(24, true);}},
            ] : []),
            // A mirrored article has no Edit here on purpose: the article is the
            // source of truth and re-publishing it overwrites the post body, so
            // an edit made in the composer would silently disappear. It is
            // edited from the news screen.
            ...(isOwner && canUpdate("post") && !isNewsArticle
              ? [{ label: t("LABEL_EDIT"), onPress: postCard.handleEdit }]
              : []),
            ...((isOwner || isSuperAdmin) && canDelete("post")
              ? [
                {
                  label: t("LABEL_DELETE"),
                  onPress: postCard.handleDelete,
                  destructive: true,
                },
              ]
              : []),
            {
              label: postCard.isSaved ? t("LABEL_UNSAVE") : t("LABEL_SAVE"),
              onPress: postCard.handleToggleSave,
            },
            { label: t("LABEL_REPORT"), onPress: () => { } },
          ]}
        />

        {follow.available && follow.mutation.isPending && <Text accessibilityLiveRegion="polite" className="px-4 text-xs text-slate">{t("FOLLOW_PENDING")}</Text>}
        {follow.available && follow.mutation.isError && <Pressable accessibilityRole="button" style={{minHeight:44}} onPress={()=>{void follow.control.refetch();}}><Text accessibilityLiveRegion="polite" className="px-4 text-xs text-crimson">{t("FOLLOW_RECONCILE")}</Text></Pressable>}
        <View testID="post-body-box" onLayout={(e) => setBodyBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}>
          <View
            className="px-4"
          >
            {!postCard.expanded && (
              // The unclamped copy, measured to decide whether "See more" is
              // needed. It carries no padding, so its height is pure text and
              // comparable against `MAX_LINES * LINE_HEIGHT`. It used to have
              // `pt-2` while the body had `pt-5 py-5`, and the two were compared
              // against each other — 32px of padding difference meant a
              // four-line post got the "…" without ever qualifying for the
              // button that reveals the rest.
              <Text
                className="text-sm leading-5 absolute opacity-0 z-[-1] text-onyx"
                onLayout={(e) =>
                  postCard.setFullHeight(e.nativeEvent.layout.height)
                }
              >
                {measureContent}
              </Text>
            )}

            {showBody && (
              // The collapsed body is clipped by its own wrapper — height
              // `MAX_LINES * LINE_HEIGHT`, overflow hidden — rather than trusting
              // the text clamp to be the only thing bounding it.
              //
              // The clamp puts the "…" at the end of line 3 and is supposed to
              // hide everything after it, but it hides overflow at the *padding*
              // box, so any bottom padding on the clamped element is room a
              // further line renders into: with `py-5` (20px) against
              // `leading-5` (20px) that was exactly one extra line, which is the
              // line that appeared below the ellipsis. The spacing is margin now
              // — outside the clip box — and this wrapper makes the three-line
              // bound structural, so nothing can render under the ellipsis
              // whatever the browser does with the clamp.
              <View
                className="my-5"
                style={
                  postCard.expanded
                    ? undefined
                    : {
                        maxHeight: postCard.MAX_LINES * postCard.LINE_HEIGHT,
                        overflow: "hidden",
                      }
                }
              >
                <CaptionText
                  className="text-sm leading-5 text-onyx"
                  numberOfLines={
                    postCard.expanded ? undefined : postCard.MAX_LINES
                  }
                  ellipsizeMode="tail"
                >
                  {postCard.expanded ? post.content : previewContent}
                </CaptionText>
              </View>
            )}

            {postCard.showSeeMore && (
              // `self-start` keeps the hover background on the label itself: a
              // Pressable in this column stretched the full card width, so
              // hovering anywhere on that row lit up the whole line.
              <Pressable
                onPress={() => postCard.setExpanded((prev) => !prev)}
                className="self-start mt-1 -ml-1 px-1.5 py-0.5 rounded-lg hover:bg-ash-3"
              >
                <Text className="text-primary text-xs font-medium">
                  {postCard.expanded
                    ? t("LABEL_SEE_LESS")
                    : t("LABEL_SEE_MORE")}
                </Text>
              </Pressable>
            )}
            {(post.hashtags ?? []).length > 0 && (
              <View className="flex-row flex-wrap justify-start w-full">
                {(post.hashtags ?? []).map((tag) => (
                  <TagPill
                    key={tag}
                    tag={tag}
                    onRemove={() => { }}
                    isEditing={false}
                    // Routed through the feed's `?search=` param rather than a
                    // callback: this card also renders in profile and group feeds,
                    // and the tag should land in the main feed's search from all of
                    // them without threading a handler through each screen.
                    onPress={(pressedTag) =>
                      router.push({
                        pathname: "/feeds",
                        params: { search: `#${pressedTag}` },
                      })
                    }
                  />
                ))}
              </View>
            )}

            {(post.mentionedUsers ?? []).length > 0 && (
              <View className="flex-row flex-wrap w-full">
                {(post.mentionedUsers ?? []).map((user) => (
                  <MentionedUserPill
                    key={user.itemId}
                    displayName={[user.firstName, user.lastName].filter(Boolean).join(" ") || "Unknown"}
                    userId={user.userId}
                    isEditing={false}
                  />
                ))}
              </View>
            )}
          </View>

          {contentMediaKind === "image" && contentUrl && (
            <View
              className="mx-4 mb-2 rounded-xl overflow-hidden"
              testID="post-content-image-preview"
            >
              <Image
                source={{ uri: contentUrl }}
                style={{ width: "100%", height: 240 }}
                resizeMode="cover"
                accessibilityIgnoresInvertColors
              />
            </View>
          )}

          {contentMediaKind === "video" && contentUrl && (
            <View className="mx-4 mb-2" testID="post-content-video-preview">
              <VideoPreview uri={contentUrl} />
            </View>
          )}

          {/* Link preview: the card is extra content — the URL itself stays in
              the body text above either way. While extraction is in flight the
              skeleton holds the card's space so the post does not jump; if it
              turns up nothing, neither is rendered. */}
          {contentMediaKind === "page" && linkPreview ? (
            <View className={showBody ? undefined : "mt-3"}>
              <LinkPreviewCard preview={linkPreview} />
            </View>
          ) : null}
          {contentMediaKind === "page" && !linkPreview && isLinkPreviewLoading ? (
            <LinkPreviewSkeleton />
          ) : null}

          {postCard.otherFiles.length > 0 && (
            <View className="px-4 pt-2">
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
              >
                {postCard.otherFiles.map((url) => (
                  <FileAttachment key={url} fileUrl={url} />
                ))}
              </ScrollView>
            </View>
          )}

          {shownMedia && cardWidth > 0 && (
            <Pressable
              disabled={stickerMode}
              onPress={() => postCard.setViewerOpen(true)}
              className={`active:opacity-90 items-center ${post.content ? "mt-2" : ""}`}
              style={{
                backgroundColor: isMobile
                  ? (themeVars["--color-chalk-default"] as string)
                  : postCard.imageBgColor,
              }}
            >
              {postCard.isFirstMediaVideo && !stickerImage ? (
                <View
                  pointerEvents="none"
                  style={{
                    width: "100%",
                    height: 350,
                    overflow: "hidden",
                    backgroundColor: themeVars["--color-ash-default"] as string,
                  }}
                >
                  <VideoPreview uri={postCard.firstMedia!} height={350} />
                </View>
              ) : (
                <View
                  testID="post-media-box"
                  onLayout={(e) => setMediaBox({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}
                  style={{
                    width: "100%",
                    maxHeight: isMobile ? 420 : 500,
                    aspectRatio: Math.max(imageAspectRatio, 0.5),
                  }}
                >
                  <Image
                    source={{ uri: shownMedia! }}
                    style={{ width: "100%", height: "100%" }}
                    onLoad={() => {
                      if (stickerImage) return;
                      Image.getSize(
                        postCard.firstMedia!,
                        (imgW, imgH) => setImageAspectRatio(imgW / imgH),
                        () => setImageAspectRatio(1),
                      );
                      postCard.handleImageLoad(postCard.firstMedia!);
                    }}
                    resizeMode="contain"
                  />
                  {stickerMode && stickerImage && mediaBox.w > 0 ? (
                    <StickerOverlay
                      session={sticker}
                      stickers={post.stickers ?? []}
                      box={containRect(mediaBox.w, mediaBox.h, imageAspectRatio)}
                    />
                  ) : null}
                  {showStickers && sticker.imageIndexes.includes(shownMediaIndex) && mediaBox.w > 0 ? (
                    <StickerLayer
                      stickers={post.stickers}
                      mediaIndex={shownMediaIndex}
                      viewerId={user?.itemId}
                      box={containRect(mediaBox.w, mediaBox.h, imageAspectRatio)}
                    />
                  ) : null}
                </View>
              )}
              {postCard.totalMediaCount > 1 && !stickerMode && (
                <View className="absolute top-3 right-3 bg-primary px-3 py-1 rounded-full">
                  <Text className="text-white text-xs">
                    1/{postCard.totalMediaCount}
                  </Text>
                </View>
              )}
            </Pressable>
          )}
          {stickerMode && !sticker.imageIndexes.length && bodyBox.w > 0 ? (
            // No image: the sticker layer covers the whole body (text, link/X preview, files).
            // Positions are fractions of this box, so reflow can shift them slightly.
            <StickerOverlay session={sticker} stickers={post.stickers ?? []} box={{ left: 0, top: 0, w: bodyBox.w, h: Math.max(bodyBox.h, 1) }} />
          ) : null}
          {showStickers && !sticker.imageIndexes.length && bodyBox.w > 0 ? (
            <StickerLayer
              stickers={post.stickers}
              mediaIndex={-1}
              viewerId={user?.itemId}
              box={{ left: 0, top: 0, w: bodyBox.w, h: Math.max(bodyBox.h, 1) }}
            />
          ) : null}
        </View>

        <PostActions
          userReaction={postCard.userReaction?.type}
          onReact={postCard.handleReact}
          reactionCounts={postCard.reactionCount}
          reactionTotal={Object.values(postCard.reactionCount || {}).reduce(
            (sum, val) => sum + (val ?? 0),
            0,
          )}
          commentCount={post.commentCount}
          onComment={() => postCard.setViewerOpen(true)}
          onShare={() => postCard.setShareModalVisible(true)}
          saveCount={post.saveCount}
          onSave={postCard.handleToggleSave}
          isSaved={postCard.isSaved}
          onReactorCount={onReactorCount}
          onSticker={stickersOn ? sticker.toggle : undefined}
          stickerActive={stickerMode}
          stickerCount={post.stickerCount ?? post.stickers?.length ?? 0}
          hasSticker={!!post.stickers?.some((s) => s.userId === user?.itemId)}
        />
        {stickerMode ? <StickerTray session={sticker} /> : null}
      </View>

      <PostDetailModal
        visible={postCard.viewerOpen}
        postId={post.ItemId}
        initialIndex={postCard.selectedIndex}
        onClose={() => postCard.setViewerOpen(false)}
      />
      <ShareModal
        visible={postCard.shareModalVisible}
        onClose={() => postCard.setShareModalVisible(false)}
        resourceId={post.ItemId}
        resource="post"
        onShare={handleShareNotification}
      />
    </View>
  );
};

export default PostCard;
